import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import setSession from '@/libs/actions/auth/set-session'
import getPartnerInfo from '@/libs/actions/get-partner-info'
import getQueryInfo from '@/libs/actions/get-query-info'
import postTrackCurrentPartnerId from '@/libs/actions/post-track-current-partner-id'
import setPartnerIdCookie from '@/libs/actions/set-partner-id-cookie'
import usePostGuestRegiserMutation from '@/libs/apis/mutations/use-post-guest-register-mutation'
import enumToString from '@/libs/helpers/enum-to-string'
import getLastCharacters from '@/libs/helpers/get-last-characters'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import useGetParams from '@/libs/hooks/use-get-params'
import { setAccountDeletion, setAccountDeletedPir } from '@/libs/store/slice/ui-slice'
import {
  setCurrentPartnerName,
  setCurrentUserAuthPartnerUrl,
  setCurrentUserPartnerId,
  setCurrentUserSigned,
  setCurrentUserUsername,
} from '@/libs/store/slice/user-slice'

const usePartner = ({ partner_id, signed }: { partner_id: string; signed: string }) => {
  const { utm_source } = useGetParams()
  const { push } = useRouter()

  const dispatch = useAppDispatch()

  const { mutate, isPending, error } = usePostGuestRegiserMutation()

  const [alert, setAlert] = useState({
    title: '',
    description: '',
    isOpen: false,
    traceId: '',
  })

  const handleGuestRegister = async () => {
    if (partner_id && signed) {
      return mutate(
        { partner_id, signed, utm_source: utm_source ?? '' },
        {
          onSuccess: async (res) => {
            const isRegistered = res?.result?.isRegistered

            const accessToken = res?.result?.accessToken
            const refreshToken = res?.result?.refreshToken
            const username = res?.result?.username

            dispatch(setCurrentUserPartnerId(partner_id))
            dispatch(setCurrentUserUsername(username))
            const authPartnerUrl = typeof window !== 'undefined' ? window.location.href : undefined

            const partnerInfo = await getPartnerInfo(partner_id)
            const partnerName = partnerInfo?.result?.fullName || partner_id

            dispatch(setCurrentPartnerName(partnerName))

            if (!isRegistered) {
              dispatch(setCurrentUserSigned(signed))
              dispatch(setCurrentUserAuthPartnerUrl(authPartnerUrl))

              return push('/auth/partner/home')
            }

            await setSession({
              accessToken,
              refreshToken,
              username,
              partnerId: partner_id,
              isGuest: 'true',
              authPartnerUrl: authPartnerUrl,
            })

            await postTrackCurrentPartnerId(partner_id)

            if (isRegistered && username) {
              const queryInfo = await getQueryInfo(username)

              if (queryInfo?.result?.isDeleteRequested) {
                dispatch(setAccountDeletion(true))
                return push('/account-deletion')
              }
            }

            return push('/home')
          },
          onError: (err: any) => {
            const { error, traceId } = err

            if (error?.code === 'USER_DELETED') {
              dispatch(setAccountDeletedPir(true))
              return
            }

            return setAlert({
              title: enumToString(error?.code),
              description: error?.message,
              isOpen: true,
              traceId: traceId && getLastCharacters(traceId),
            })
          },
        },
      )
    } else {
      return setAlert({
        title: 'ERROR OCCURED',
        description: 'Missing Partner ID',
        isOpen: true,
        traceId: '',
      })
    }
  }

  const redirectToLogin = () => {
    return push('/auth/login')
  }

  useEffect(() => {
    handleGuestRegister()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPartnerIdCookie(partner_id, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isPending,
    error,
    redirectToLogin,
    alert,
    handleGuestRegister,
  }
}

export default usePartner
